/**
 * GPU 上下文丢失与恢复（DESIGN.md §2.8）。
 *
 * WebGPU 的 device lost 与 WebGL2 的 `webglcontextlost` 是同一件事的两种说法，
 * 这里用一套状态机处理：
 *
 * ```
 *   ready ──丢失──> lost ──开始恢复──> recovering ──成功──> ready
 *                                          └──失败──> failed
 * ```
 *
 * **恢复期间提交的渲染请求排队而不是丢弃**。丢弃会让用户看到一帧空白或旧内容，
 * 而抛异常会让调用方在一个完全可恢复的情况下崩掉。排队则让调用点写起来
 * 就像什么都没发生——这也是把恢复逻辑放在这一层、而不是每个调用点各写一遍的原因。
 */
import type { ResourceRegistry } from './resource-registry';

export type GpuContextState = 'ready' | 'lost' | 'recovering' | 'failed';

export interface RecoveryHooks {
  /** 重新拿到一个可用的底层上下文/设备。失败应抛异常或返回 false。 */
  reacquire(): Promise<boolean>;
  /** 状态变化通知，用于 UI 提示（"正在恢复显卡资源…"）。 */
  onStateChange?(state: GpuContextState, detail?: string): void;
}

export interface RecoveryOptions {
  readonly registry: ResourceRegistry;
  readonly hooks: RecoveryHooks;
  /** 恢复失败后的重试次数上限。超过则进入 failed，不再无限重试。 */
  readonly maxAttempts?: number;
}

/** 恢复失败后仍继续提交渲染时抛出。 */
export class GpuUnrecoverableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'GpuUnrecoverableError';
  }
}

/**
 * 上下文恢复协调器。
 *
 * 用法：把所有渲染提交包进 {@link submit}，把丢失事件接到 {@link notifyLost}。
 */
export class GpuContextRecovery {
  #state: GpuContextState = 'ready';
  #pending: {
    run: () => unknown;
    resolve: (v: unknown) => void;
    reject: (e: unknown) => void;
  }[] = [];
  #recovering: Promise<void> | undefined;
  #attempts = 0;

  constructor(private readonly options: RecoveryOptions) {}

  get state(): GpuContextState {
    return this.#state;
  }

  /** 恢复期间积压的请求数。 */
  get queuedCount(): number {
    return this.#pending.length;
  }

  get recreateAttempts(): number {
    return this.#attempts;
  }

  #setState(state: GpuContextState, detail?: string): void {
    this.#state = state;
    this.options.hooks.onStateChange?.(state, detail);
  }

  /**
   * 提交一次渲染工作。
   *
   * - `ready`：立即执行。
   * - `lost` / `recovering`：排队，恢复完成后按提交顺序执行。
   * - `failed`：拒绝，抛 {@link GpuUnrecoverableError}。
   */
  async submit<T>(work: () => T | Promise<T>): Promise<T> {
    if (this.#state === 'failed') {
      throw new GpuUnrecoverableError('GPU 上下文无法恢复，渲染请求被拒绝。');
    }

    if (this.#state === 'ready') {
      return await work();
    }

    // 排队而非丢弃：丢弃会让用户看到空白帧，抛异常会让调用点在可恢复的情况下崩掉。
    return await new Promise<T>((resolve, reject) => {
      this.#pending.push({
        run: work as () => unknown,
        resolve: resolve as (v: unknown) => void,
        reject,
      });
    });
  }

  /**
   * 通知上下文已丢失，并启动恢复。
   *
   * 重复通知是安全的（浏览器可能连发多次事件），恢复只会进行一轮。
   */
  notifyLost(detail?: string): Promise<void> {
    if (this.#state === 'failed') return Promise.resolve();
    if (this.#recovering !== undefined) return this.#recovering;

    this.#setState('lost', detail);
    this.#recovering = this.#recover().finally(() => {
      this.#recovering = undefined;
    });
    return this.#recovering;
  }

  async #recover(): Promise<void> {
    const maxAttempts = this.options.maxAttempts ?? 3;

    while (this.#attempts < maxAttempts) {
      this.#attempts++;
      this.#setState('recovering', `第 ${this.#attempts} 次尝试`);

      try {
        const ok = await this.options.hooks.reacquire();
        if (!ok) continue;

        const { failures } = this.options.registry.recreateAll();
        if (failures.length > 0) {
          // 部分资源没能重建，整体不算恢复——否则会渲染出缺图层的画面。
          throw new Error(
            `${failures.length} 份资源重建失败：${failures.map((f) => f.label).join(', ')}`,
          );
        }

        this.#setState('ready');
        this.#flush();
        return;
      } catch (error) {
        if (this.#attempts >= maxAttempts) {
          this.#setState('failed', String(error));
          this.#rejectPending(
            new GpuUnrecoverableError(`GPU 上下文恢复失败（已尝试 ${this.#attempts} 次）：${String(error)}`),
          );
          return;
        }
      }
    }

    this.#setState('failed', '超过最大重试次数');
    this.#rejectPending(new GpuUnrecoverableError('GPU 上下文恢复失败：超过最大重试次数。'));
  }

  /** 按提交顺序放行积压的请求。 */
  #flush(): void {
    const queued = this.#pending;
    this.#pending = [];
    for (const item of queued) {
      try {
        Promise.resolve(item.run()).then(item.resolve, item.reject);
      } catch (error) {
        item.reject(error);
      }
    }
  }

  #rejectPending(error: unknown): void {
    const queued = this.#pending;
    this.#pending = [];
    for (const item of queued) item.reject(error);
  }
}

/**
 * 把 WebGL2 的上下文丢失事件接到恢复协调器。
 *
 * `webglcontextlost` 必须 `preventDefault()`，否则浏览器不会触发
 * `webglcontextrestored`——这是 WebGL 规范里一个极易漏掉的细节，
 * 漏了的表现是"上下文丢了就再也回不来"。
 *
 * @returns 解绑函数。
 */
export function bindWebGLContextLoss(
  canvas: {
    addEventListener(type: string, listener: (event: Event) => void): void;
    removeEventListener(type: string, listener: (event: Event) => void): void;
  },
  recovery: GpuContextRecovery,
): () => void {
  const onLost = (event: Event): void => {
    event.preventDefault();
    void recovery.notifyLost('webglcontextlost');
  };
  // restored 事件本身不触发恢复：重建由 notifyLost 里的 reacquire 驱动，
  // 两处都发起会导致资源被重建两次。
  canvas.addEventListener('webglcontextlost', onLost);
  return () => {
    canvas.removeEventListener('webglcontextlost', onLost);
  };
}

/**
 * 把 WebGPU 的 device lost 接到同一套恢复流程。
 *
 * `device.lost` 是个只会 resolve 一次的 Promise，因此每次拿到新 device 后
 * 都必须重新调用本函数——这也是它接受 `getDevice` 而不是 device 的原因。
 */
export function bindWebGPUDeviceLost(
  device: { lost: Promise<{ reason: string; message: string }> },
  recovery: GpuContextRecovery,
): void {
  void device.lost.then((info) => {
    // reason === 'destroyed' 是我们主动 destroy() 的正常关闭，不该触发恢复。
    if (info.reason === 'destroyed') return;
    void recovery.notifyLost(`device lost: ${info.message}`);
  });
}
